import { goApi } from '../../services';

export const restDataProvider = {
    getList: async (resource: string, params: any) => {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const allowedSortFields = ['displayOrder', 'createdAt'];
        const filters = params.filter || {};
        const query = new URLSearchParams({
            page,
            limit: perPage,
            ...filters,
            ...(allowedSortFields.includes(field) ? { orderBy: field } : {}),
            ...(allowedSortFields.includes(field) && order ? { order: order } : {}),
        }).toString();

        const url = `/${resource}?${query}`;
        const json = await goApi.get(url);

        const items = json?.data?.items;
        if (!Array.isArray(items)) {
            return Promise.reject(`Invalid API response for ${resource}`);
        }

        return {
            data: items.map((rec: any) => ({
                ...rec,
                id: rec.id,
            })),
            total: json.data.pagination?.total ?? items.length,
        };
    },

    getMany: async (resource: string, params: any) => {
        const records = await Promise.all(
            params.ids.map((id: string) => goApi.get(`/${resource}/${id}`)),
        );

        return {
            data: records.map((record: any) => record.data),
        };
    },

    getManyReference: async (resource: string, params: any) => {
        return restDataProvider.getList(resource, {
            ...params,
            filter: {
                ...(params.filter || {}),
                [params.target]: params.id,
            },
        });
    },

    getOne: async (resource: string, params: any) => {
        const url = `/${resource}/${params.id}`;
        const json = await goApi.get(url);

        if (!json?.data || !json.data.id) {
            return Promise.reject(`Invalid getOne response for ${resource}`);
        }

        return { data: json.data };
    },

    create: async (resource: string, params: any) => {
        const json = await goApi.post(`/${resource}`, params.data);

        if (!json?.data || !json.data.id) {
            return Promise.reject(`Invalid create response for ${resource}`);
        }

        return { data: json.data };
    },

    update: async (resource: string, params: any) => {
        const url = `/${resource}/${params.id}`;
        const json = await goApi.put(url, params.data);

        if (!json?.data || !json.data.id) {
            return Promise.reject(`Invalid update response for ${resource}`);
        }

        return { data: json.data };
    },

    updateMany: async (resource: string, params: any) => {
        await Promise.all(
            params.ids.map((id: string) => goApi.put(`/${resource}/${id}`, params.data)),
        );

        return { data: params.ids };
    },

    delete: async (resource: string, params: any) => {
        const url = `/${resource}/${params.id}`;

        try {
            await goApi.delete(url);
            return { data: params.previousData ?? { id: params.id } };
        } catch (error: any) {
            console.error('Delete failed:', error);
            throw new Error(error.message || 'Failed to delete resource');
        }
    },

    deleteMany: async (resource: string, params: any) => {
        await Promise.all(params.ids.map((id: string) => goApi.delete(`/${resource}/${id}`)));
        return { data: params.ids };
    },
};
